"use client";

import {
  Else,
  ElseIf,
  If,
} from "@nyntax/design-system/components/ui/conditional";
import {
  Table,
  TableBody,
  TableCell,
  TableRow,
} from "@nyntax/design-system/components/ui/table";
import { cn } from "@nyntax/design-system/lib/utils";
import { flexRender, type Row } from "@tanstack/react-table";
import type { ReactNode } from "react";
import { useDataTable } from "./data-table-context";
import { DataTableHeader } from "./data-table-header";

interface DataTableContentProps<TData = unknown> {
  className?: string;
  emptyMessage?: string;
  emptyState?: ReactNode;
  renderExpandedRow?: (row: Row<TData>) => ReactNode;
  /** Extra className per row, e.g. a status accent border. */
  rowClassName?: (row: Row<TData>) => string | undefined;
}

function getClickHandler<TData>(expandable: boolean, row: Row<TData>) {
  if (!expandable) {
    return;
  }
  return row.getToggleExpandedHandler();
}

function getRowClassName(
  expandable: boolean,
  expanded: boolean,
  extra: string | undefined
) {
  const classes = ["transition-colors duration-150"];
  if (expandable) {
    classes.push("cursor-pointer");
  }
  if (expanded) {
    classes.push("bg-muted/50");
  }
  if (extra) {
    classes.push(extra);
  }
  return classes.join(" ");
}

function DataTableRow<TData>({
  row,
  columnCount,
  expandable,
  renderExpandedRow,
  rowClassName,
}: {
  row: Row<TData>;
  columnCount: number;
  expandable: boolean;
  renderExpandedRow?: (row: Row<TData>) => ReactNode;
  rowClassName?: (row: Row<TData>) => string | undefined;
}) {
  const isExpanded = row.getIsExpanded();

  return (
    <>
      <TableRow
        className={getRowClassName(expandable, isExpanded, rowClassName?.(row))}
        data-state={row.getIsSelected() ? "selected" : null}
        onClick={getClickHandler(expandable, row)}
      >
        {row.getVisibleCells().map((cell) => (
          <TableCell key={cell.id}>
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        ))}
      </TableRow>
      <If condition={isExpanded}>
        <TableRow>
          <TableCell className="p-0" colSpan={columnCount}>
            {renderExpandedRow?.(row)}
          </TableCell>
        </TableRow>
      </If>
    </>
  );
}

function DataTableContent<TData>({
  emptyMessage = "No results.",
  emptyState,
  className,
  renderExpandedRow,
  rowClassName,
}: DataTableContentProps<TData>) {
  const table = useDataTable<TData>();
  const rows = table.getRowModel().rows;
  const columnCount = table.getAllColumns().length;
  const expandable = !!renderExpandedRow;

  return (
    <Table
      className={cn(
        "w-auto min-w-full table-auto",
        "[&_th:not(:last-child)]:border-border [&_th:not(:last-child)]:border-r",
        "[&_td:not(:last-child)]:border-border [&_td:not(:last-child)]:border-r",
        className
      )}
    >
      <DataTableHeader />
      <TableBody className="[&_tr:last-child]:border-b">
        <If condition={rows.length > 0}>
          {rows.map((row) => (
            <DataTableRow
              columnCount={columnCount}
              expandable={expandable}
              key={row.id}
              renderExpandedRow={renderExpandedRow}
              row={row}
              rowClassName={rowClassName}
            />
          ))}
          <ElseIf condition={!!emptyState}>
            <TableRow>
              <TableCell className="p-0" colSpan={columnCount}>
                {emptyState}
              </TableCell>
            </TableRow>
          </ElseIf>
          <Else>
            <TableRow>
              <TableCell className="h-24 text-center" colSpan={columnCount}>
                {emptyMessage}
              </TableCell>
            </TableRow>
          </Else>
        </If>
      </TableBody>
    </Table>
  );
}

export type { DataTableContentProps };
export { DataTableContent };
